'use client';

import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useEffect, useRef, useState } from 'react';

import { Header } from '@/widgets/header';

/** Через сколько шапка уезжает вверх, если её не трогают. */
const AUTO_HIDE_MS = 3000;

/**
 * Шапка экрана интервью, которая сама убирается наверх.
 *
 * Экран интервью занимает всю высоту, и постоянная шапка съедает вертикаль,
 * которой в чате мало. Поэтому она уезжает через `AUTO_HIDE_MS`, а вернуть
 * её можно стрелкой по центру. Пока курсор над шапкой, таймер не тикает —
 * иначе она уезжала бы из-под руки.
 */
export function InterviewHeader({ children }: { children: React.ReactNode }) {
	const [visible, setVisible] = useState(true);
	const [hovered, setHovered] = useState(false);
	// Автоскрытие срабатывает один раз при заходе. Дальше шапкой управляет
	// пользователь: прятать то, что он только что открыл сам, — навязчиво.
	const autoHiddenRef = useRef(false);

	useEffect(() => {
		if (autoHiddenRef.current || !visible || hovered) return;

		const timer = setTimeout(() => {
			autoHiddenRef.current = true;
			setVisible(false);
		}, AUTO_HIDE_MS);

		return () => clearTimeout(timer);
	}, [visible, hovered]);

	const toggle = () => {
		// Ручное переключение отменяет отложенное автоскрытие.
		autoHiddenRef.current = true;
		setVisible((prev) => !prev);
	};

	return (
		<div className="relative flex h-[100svh] w-full flex-col overflow-hidden">
			<div
				onMouseEnter={() => setHovered(true)}
				onMouseLeave={() => setHovered(false)}
				className={`shrink-0 transition-[margin-top,opacity] duration-300 ease-out ${
					visible
						? 'mt-0 opacity-100'
						: 'pointer-events-none -mt-[var(--header-height)] opacity-0'
				}`}
			>
				<Header />
			</div>

			<div className="relative min-h-0 flex-1">
				{children}

				<button
					type="button"
					onClick={toggle}
					aria-expanded={visible}
					aria-label={visible ? 'Скрыть шапку' : 'Показать шапку'}
					title={visible ? 'Скрыть шапку' : 'Показать шапку'}
					className="absolute left-1/2 top-0 z-40 flex h-3.5 w-8 -translate-x-1/2 items-center justify-center rounded-b-md border border-t-0 border-gray-4 bg-white/90 text-gray-3 shadow-sm backdrop-blur transition-colors hover:text-gray-1"
				>
					<ChevronDownIcon
						className={`size-3 transition-transform duration-300 ${
							visible ? 'rotate-180' : ''
						}`}
					/>
				</button>
			</div>
		</div>
	);
}
